import { useState } from "react"
import { Globe, Check } from "lucide-react"
import type { Locale } from "@/lib/i18n"

interface LanguageSwitcherProps {
  locale: Locale
  onLocaleChange: (locale: Locale) => void
}

export function LanguageSwitcher({ locale, onLocaleChange }: LanguageSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false)
  const locales: Locale[] = ["uz", "ru", "en"]
  const localeLabels: Record<Locale, string> = {
    uz: "🇺🇿 Uzbek",
    ru: "🇷🇺 Русский",
    en: "🇺🇸 English",
  }

  const localeFullNames: Record<Locale, string> = {
    uz: "🇺🇿 Uzbek",
    ru: "🇷🇺 Русский",
    en: "🇺🇸 English",
  }

  const handleLocaleChange = (newLocale: Locale) => {
    onLocaleChange(newLocale)
    setIsOpen(false)
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground/80 hover:text-foreground hover:bg-foreground/5 rounded-lg transition-all duration-200 group"
        aria-label="Change language"
        aria-expanded={isOpen}
      >
        <Globe className="w-4 h-4 group-hover:text-primary transition-colors" />
        <span className="font-semibold">{localeLabels[locale]}</span>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-40 bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden animate-scale-in">
          {locales.map((loc) => (
            <button
              key={loc}
              onClick={() => handleLocaleChange(loc)}
              className={`w-full px-4 py-3 text-left text-sm font-medium transition-all duration-200 flex items-center justify-between group ${
                locale === loc
                  ? "bg-primary/10 text-primary border-l-2 border-primary"
                  : "text-foreground/80 hover:text-foreground hover:bg-foreground/5"
              }`}
              aria-current={locale === loc ? "true" : "false"}
            >
              <span>{localeFullNames[loc]}</span>
              {locale === loc && <Check className="w-4 h-4 text-primary" />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
